import { Analytics } from '@util/Analytics';
import { translate } from '@util/Localization';
import React, { FunctionComponent, useState } from 'react';
import styled from 'styled-components';
import { Color } from '../../../config/Color';
import { ConsentCheckbox } from '../../styles/ConsentCheckbox';
import { FlowContentBody, QuestionWrapper } from '../../styles/Containers';
import { BodySM, HeroTitle } from '../../styles/Typography';
import { FullDetails } from './FullDetails';
import { LayoutActions } from './LayoutActions';

/** The boxes on the screen. The AU consent is the AU health plan authorization, so `healthPlan` too. */
export type CoverageConsent = 'healthPlan' | 'assignmentOfBenefits' | 'employer';

/** Which boxes are ticked; a box left out is unticked. */
export type CoverageConsentTicks = Partial<Record<CoverageConsent, boolean>>;

/** One optional consent: a one-line summary, with the full legal text behind a toggle. */
export type CoverageConsentConfig = {
  consent: CoverageConsent;
  summary: string;
  /** Full legal text, one paragraph per entry. */
  fullText: string[];
  /** AU: links the privacy policy after the full text. */
  privacyPolicyTitle?: string;
};

export type CoverageConsentsLayoutConfig = {
  title: string;
  body: string;
  showFullTitle: string;
  hideFullTitle: string;
  primaryButtonTitle: string;
  /** In the order shown. The employer consent is only offered on the employer path. */
  us: CoverageConsentConfig[];
  au: CoverageConsentConfig[];
};

type Props = Omit<CoverageConsentsLayoutConfig, 'us' | 'au'> & {
  /** The consents offered to this user. */
  consents: CoverageConsentConfig[];
  prefill: CoverageConsentTicks;
  onPressPrimaryButton: (ticks: CoverageConsentTicks) => void;
};

export const CoverageConsentsLayout: FunctionComponent<Props> = (props) => {
  const [ticks, setTicks] = useState(props.prefill);

  return (
    <QuestionWrapper>
      <FlowContentBody>
        <HeroTitle data-test="surveyQuestion">{props.title}</HeroTitle>
        <BodySM>{props.body}</BodySM>
        {props.consents.map(({ consent, summary, fullText, privacyPolicyTitle }) => (
          <ConsentCheckbox
            key={consent}
            checked={ticks[consent] === true}
            onChange={(checked) => setTicks((current) => ({ ...current, [consent]: checked }))}
            label={summary}
            testId={`coverage-consent-${consent}`}
          >
            <FullDetails
              showFullTitle={props.showFullTitle}
              hideFullTitle={props.hideFullTitle}
              fullText={fullText}
              testId={`coverage-consent-${consent}-details`}
            >
              {privacyPolicyTitle !== undefined && (
                <PrivacyPolicyLink
                  href={translate('links.privacy')}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => Analytics.track({ event: Analytics.Event.o_privacy_opened })}
                  data-testid="coverage-privacy-policy"
                >
                  {privacyPolicyTitle}
                </PrivacyPolicyLink>
              )}
            </FullDetails>
          </ConsentCheckbox>
        ))}
      </FlowContentBody>
      <LayoutActions
        primaryButtonTitle={props.primaryButtonTitle}
        onPressPrimaryButton={() => props.onPressPrimaryButton(ticks)}
      />
    </QuestionWrapper>
  );
};

const PrivacyPolicyLink = styled.a`
  display: inline-block;
  color: ${Color.content.default};
  font-size: ${({ theme }) => theme.fontSize.xs};
  text-decoration: underline;
  &:hover {
    color: ${Color.content.default};
  }
`;
